import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import './pack_logos.css';
import { ArrowLeft, Info } from 'lucide-react';
import { packConfig } from '../packConfig';
import Popup_Packs from '../packnvpu/packs';

const PackLogos = ({ packType, onClose }) => {
  const [isPopupOpen, setIsPopupOpen] = useState({});
  const [selectedPack, setSelectedPack] = useState(null);
  const [validPacks, setValidPacks] = useState([]);
  const [packCounts, setPackCounts] = useState({});
  const [error, setError] = useState(null);
  const [selectedPackName, setSelectedPackName] = useState(null);
  const [rect, setRect] = useState(null);
  const popupRef = useRef(null);
  const API_URL = process.env.REACT_APP_API_URL;

  const packs = packConfig[packType] || [];

  // Mesure la zone du dashboard pour limiter le flou à cette zone
  useLayoutEffect(() => {
    const measure = () => {
      const el = document.querySelector('.overlayClient');
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const backdropStyle = rect
    ? {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        right: 'auto',
        bottom: 'auto',
      }
    : undefined;

  const popupStyle = rect
    ? {
        top: rect.top + rect.height / 2,
        left: rect.left + rect.width / 2,
      }
    : undefined;

  useEffect(() => {
    const initialPopupState = {};
    packs.forEach((pack) => { initialPopupState[pack.name] = false; });
    setIsPopupOpen(initialPopupState);
  }, [packType]);

  useEffect(() => {
    let isMounted = true;
    const fetchPackCounts = async () => {
      const userId = localStorage.getItem('userId');
      if (!userId) { if (isMounted) setError('Utilisateur non identifié'); return; }
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        const res = await fetch(`${API_URL}/payments/count-by-pack/${userId}`, { signal: controller.signal });
        if (!res.ok) throw new Error('Erreur réseau');
        const data = await res.json();
        if (isMounted) {
          const normalized = {};
          packs.forEach((pack) => {
            const key = pack.name.toLowerCase();
            const match = Object.entries(data.packCounts).find(([k]) => k.toLowerCase() === key);
            normalized[key] = match ? match[1] : { totalQuantity: 0, dates: [] };
          });
          setPackCounts(normalized);
        }
        clearTimeout(timeoutId);
      } catch (err) { if (isMounted) setError('Erreur de chargement'); }
    };
    fetchPackCounts();
    return () => { isMounted = false; };
  }, [API_URL, packType]);

  useEffect(() => {
    let isMounted = true;
    const fetchValidPacks = async () => {
      const userId = localStorage.getItem('userId');
      if (!userId) return;
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        const res = await fetch(`${API_URL}/payments/valid-packs/${userId}`, { signal: controller.signal });
        if (!res.ok) throw new Error('Erreur réseau');
        const data = await res.json();
        if (isMounted) setValidPacks(data.validPacks.map(p => p.toLowerCase()));
        clearTimeout(timeoutId);
      } catch { if (isMounted) setValidPacks([]); }
    };
    fetchValidPacks();
    return () => { isMounted = false; };
  }, [API_URL, packType]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        closePopup(); onClose();
      }
    };
    const anyOpen = Object.values(isPopupOpen).some(Boolean);
    if (anyOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPopupOpen, onClose]);

  const handleLogoClick = (pack) => {
    if (!validPacks.includes(pack.name.toLowerCase())) return;
    closeAllPopups();
    setSelectedPack(pack);
    setSelectedPackName(pack.name);
    setIsPopupOpen((prev) => ({ ...prev, [pack.name]: true }));
    localStorage.setItem('packName', pack.name);
  };

  const closePopup = () => {
    const reset = {};
    Object.keys(isPopupOpen).forEach((k) => { reset[k] = false; });
    setIsPopupOpen(reset);
    setSelectedPack(null);
    setSelectedPackName(null);
  };

  const closeAllPopups = () => closePopup();

  return createPortal(
    <>
      {/* Backdrop flou (limité à la zone du dashboard) */}
      <div className="pack-backdrop" style={backdropStyle} onClick={onClose} />

      <div className="pack-overlay" ref={popupRef} style={popupStyle}>
        {Object.values(isPopupOpen).some(Boolean) && selectedPack ? (
          <div className="detail-view">
            <button className="back-btn" onClick={closePopup} aria-label="Retour">
              <ArrowLeft size={20} />
            </button>
            <Popup_Packs
              packId={packs.find((p) => p.name === selectedPack.name)?.id || 1}
              selectedPack={selectedPack}
              packName={selectedPackName}
              onClose={closePopup}
            />
          </div>
        ) : (
          <div className="pack-grid">
            {packs.map((pack, index) => {
              const key = pack.name.toLowerCase();
              const quantity = packCounts[key]?.totalQuantity || 0;
              const isValid = validPacks.includes(key);
              return (
                <div
                  key={index}
                  className={`pack-card ${isValid ? 'valid' : 'disabled'}`}
                  onClick={() => handleLogoClick(pack)}
                >
                  <div className="pack-img-wrap">
                    <img
                      src={`/images/packs/unlimitedBonus/${pack.name.replace(' ', '_')}.jpg`}
                      alt={pack.name}
                      className="pack-img"
                    />
                    <span className={`pack-badge ${!isValid ? 'muted' : ''}`}>
                      {error ? 'Erreur' : `${quantity} Packs`}
                    </span>
                  </div>
                  <div className="pack-footer">
                    <span className="pack-name">{pack.name}</span>
                    <button
                      className="info-btn"
                      disabled={!isValid}
                      onClick={(e) => { e.stopPropagation(); handleLogoClick(pack); }}
                      aria-label={`Détails ${pack.name}`}
                    >
                      <Info size={15} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>,
    document.body
  );
};

export default React.memo(PackLogos);